import React, { SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BASE_URL,
  BLACK,
  PRIMARY,
  SECONDARY,
  STATUS,
  WHITE,
} from "../../appConstants";
import {
  Button,
  Input,
  Option,
  Select,
  selectClasses,
  Skeleton,
  DialogContent,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/joy";
import RemoveBrandModal from "./RemoveBrandModal";
import SaveChangesModal from "./SaveChangesModal";
import UserPlatformEditModal from "./UserPlatformEditModal";
import axios from "axios";
import appError from "../../appError";
import Joi, { boolean } from "joi";
import toast from "react-hot-toast";
import SubDashboardsForEdit from "./SubDashboardsForEdit";
import { Dispatch } from "@reduxjs/toolkit";
import { previousDay } from "date-fns";

type SubDashboard = {
  sub_module_for_dashboard_type: string;
  sub_module_for_dashboard_type_id: string;
};

type Dashboard = {
  color: string;
  dashboard_type_id: string;
  dashboard_type_name: string;
  dashboard_type_sub_module?: SubDashboard[];
};

type Sub_ModuleForPlatform = {
  sub_module_for_platform: string;
  sub_module_for_platform_id: string;
}

type Platform = {
  platform_id: string;
  platform_name: string;
  dashboard_type: Dashboard[];
  platform_sub_module_array: Sub_ModuleForPlatform[];
};

type Sub_Module = {
  sub_module_for_brand: string;
  sub_module_for_brand_id: string;
};

type Brand_det = {
  brand_id: string;
  brand_name: string; // This property is in Brand_det, but not in EditBrand_det
  platform_array: Platform[];
  brand_sub_module_array: Sub_Module[];
};

type User = {
  full_name: string;
  status: string;
  user_type: string;
  email: string;
  organisation: string;
  contact_number: string;
  brand_array: Brand_det[];
};

type Remove = {
  remove_toggle: boolean;
  remove_name: string;
  remove_type: string;
  remove_brand_id: string;
  remove_platform_id: string;
  remove_dashboard_id: string;
};

type Brand = {
  brand_id: string;
  brand_name: string;
};
type SuggestedDashboard = {
  brand_power_bi_dashboard_type_id: string;
  dashboard_type_name: string;
  color: string;
};
type SuggestBrand = {
  brand_id: string;
  brand_name: string;
};


const UserDetailEditNew: React.FC = () => {
  const navigate = useNavigate();
  const { user_id } = useParams<{ user_id: string }>();
  const [cancelToggle, setCancelToggle] = useState<boolean>(false);
  const [DataChanged, setDataChanged] = useState<boolean>(false); //Check if Data has been Changed or Not
  const [toggleModal, setToggleModal] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [view, setView] = useState<string>("");
  const [selectedBrandId, setSelectedBrandId] = useState<string>(""); // For Selecting Platforms in Add Platforms
  const [selPlatforms, setSelPlatforms] = useState<string[]>([]); // For filtering platfroms in Add Dashboard
  const [loading, setLoading] = useState<boolean>(false); // Only used for save user api in Edit Details Page
  const [dataFetchingState, setDataFetchingState] = useState<string>("LOADING");

  // Initial State of Remove Datat for Remove Modal
  const [removedata, setRemoveData] = useState<Remove>({
    remove_toggle: false,
    remove_name: "",
    remove_type: "",
    remove_brand_id: "",
    remove_platform_id: "",
    remove_dashboard_id: "",
  });

  // On Clicking Discard Button in SaveChanges Modal cancelSaveChangesToggle function will called & it will navigate to userdetailview page
  const cancelSaveChangesToggle = () => {
    setCancelToggle(false);
    navigate(`/userManagement/userDetailView/${user_id}`);
  };

  // AddBrandsToggle Function is used For Add Brands Modal
  const addBrandstoggle = () => {
    setToggleModal(true);
    setView("add_brand");
  };

  // AddPlatformsToggle Function is used For Add Platforms Modal
  const addPlatformstoggle = (id: string) => {
    setToggleModal(true);
    setSelectedBrandId(id);
    setView("add_platform");
  };

  // AddDashboardToggle Function is used For Add Dashboard Modal
  const addDashboardtoggle = (brandid: string, platformid: string) => {
    setToggleModal(true);
    setSelectedBrandId(brandid);
    setSelPlatforms([platformid]);
    setView("add_dashboard");
  };

  // Initial state for User
  const [user, setUser] = useState<User>({} as User);

  // HandleChange function for changing Full Name, Email, Contact Number, Organisation fields.
  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
    field: keyof User
  ) => {
    const { value } = e.target;
    setUser((prevUser) => ({
      ...prevUser,
      [field]: value,
    }));
    setDataChanged(true);
  };

  // For Brands Assign section in Basic Details
  const [brands, setBrands] = useState<Brand[]>([]);

  // Remove Brand function for removing brand in Remove Modal -- Error
  const removeBrand = (brandIdToRemove: string) => {
    if (user.brand_array?.length === 0) {
      appError("There are No Brands To Remove");
      return;
    }
    setDataChanged(true);
    setBrands((prev) =>
      prev.filter((brand) => brand.brand_id !== brandIdToRemove)
    );
    setUser({
      ...user,
      brand_array: user.brand_array.filter(
        (brand) => brand.brand_id !== brandIdToRemove
      ),
    });
  };

  // Remove Platform function for removing Platform in Remove Modal
  const removeplatform = (brandid: string, platformid: string) => {
    const updatedBrandArray = user.brand_array.map((brand) => {
      // Check if the brand_id matches the input brandid
      if (brand.brand_id === brandid) {
        // Filter the platform_array to exclude the platform with the matching platformid
        return {
          ...brand,
          platform_array: brand.platform_array.filter(
            (platform) => platform.platform_id !== platformid
          ),
        };
      }
      return brand; // Return the brand unchanged if ids do not match
    });
    setUser({
      ...user,
      brand_array: updatedBrandArray,
    });
    setDataChanged(true);
  };

  // Remove Dashboard function for removing Dashboard in Remove Modal
  const removeDashboard = (
    brandid: string,
    platformid: string,
    dashboardid: string
  ) => {
    const updatedBrandArray = user.brand_array.map((brand) => {
      // Check if the brand_id matches the input brandid
      if (brand.brand_id === brandid) {
        // Filter the platform_array
        const updatedPlatformArray = brand.platform_array.map((platform) => {
          // Check if the platform_id matches the input platformid
          if (platform.platform_id === platformid) {
            // Filter the dashboard_type to exclude the dashboard with the matching dashboardid
            return {
              ...platform,
              dashboard_type: platform.dashboard_type.filter(
                (dashboard) => dashboard.dashboard_type_id !== dashboardid
              ), // Adjust `id` based on your dashboard object structure
            };
          }
          return platform; // Return the platform unchanged if ids do not match
        });

        return {
          ...brand,
          platform_array: updatedPlatformArray,
        };
      }
      return brand; // Return the brand unchanged if ids do not match
    });

    // Update the user state
    setUser({
      ...user,
      brand_array: updatedBrandArray,
    });
    setDataChanged(true);
  };

  // RemoveBrandHandler function for storing all data in removedata object for Remove Modal
  const RemoveBrandHandler = (brandid: string, name: string) => {
    setRemoveData({
      ...removedata,
      remove_toggle: true,
      remove_name: name,
      remove_type: "Brand",
      remove_brand_id: brandid,
    });
  };

  // RemovePlatformHandler function for storing all data in removedata object for Remove Modal
  const RemovePlatformHandler = (
    brandid: string,
    name: string,
    platformid: string
  ) => {
    setRemoveData({
      ...removedata,
      remove_toggle: true,
      remove_name: name,
      remove_type: "Platform",
      remove_brand_id: brandid,
      remove_platform_id: platformid,
    });
  };

  // RemoveDashboardHandler function for storing all data in removedata object for Remove Modal
  const RemoveDashboardHandler = (
    brandid: string,
    name: string,
    platformid: string,
    dashboardid: string
  ) => {
    setRemoveData({
      remove_toggle: true,
      remove_name: name,
      remove_type: "Dashboard",
      remove_brand_id: brandid,
      remove_platform_id: platformid,
      remove_dashboard_id: dashboardid,
    });
  };

  // Remove Function is called on clicking Remove button in Remove Modal it checks if remove type in RemoveData object is Brand Than it calls RemoveBrand function & same for Platform & Dashboard. Atlast sets RemoveData object to its initial State.
  const remove = () => {
    if (removedata.remove_type === "Brand") {
      removeBrand(removedata.remove_brand_id);
    } else if (removedata.remove_type === "Platform") {
      removeplatform(removedata.remove_brand_id, removedata.remove_platform_id);
    } else {
      removeDashboard(
        removedata.remove_brand_id,
        removedata.remove_platform_id,
        removedata.remove_dashboard_id
      );
    }
    setRemoveData({
      ...removedata,
      remove_toggle: false,
      remove_name: "",
      remove_type: "",
      remove_brand_id: "",
      remove_platform_id: "",
      remove_dashboard_id: "",
    });
  };

  // This saveUpdatedUser function is used for sending user Data to PUT API & called in saveuser function & after validating user object
  const saveupdatedUser = async (user: User) => {
    try {
      const {
        full_name,
        email,
        contact_number,
        user_type,
        status: mainstatus,
        organisation,
        brand_array,
      } = user;
      setLoading(true);

      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${BASE_URL}user/UserPlatformDashboardMapping`,
        {
          full_name,
          email,
          contact_number,
          user_type,
          status: mainstatus,
          organisation,
          brand_array,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { status } = res?.data;
      setDataChanged(false);
      toast.success(status);
    } catch (error) {
      appError(error);

    } finally {
      setLoading(false);
    }
  };

  // SaveUser function is used for validating user object & after it calls saveupdatedUser first it checks if Data is changed or Not.
  const saveuser = () => {
    if (!DataChanged) {
      appError("Please Change Details");
      return;
    }
    const SubDashboardTypeSchema = Joi.object({
      sub_module_for_dashboard_type_id: Joi.string()
        .guid({ version: "uuidv4" })
        .required(),
      sub_module_for_dashboard_type: Joi.string().label("Sub Dashboard Name"),
    });
    const dashboardTypeSchema = Joi.object({
      color: Joi.string().required(),
      dashboard_type_id: Joi.string().guid({ version: "uuidv4" }).required(),
      dashboard_type_name: Joi.string().label("Dashboard Name"),
      dashboard_type_sub_module: Joi.array()
        .items(SubDashboardTypeSchema)
        .required(),
    });

    // Define the schema for platform_array
    const platformSchema = Joi.object({
      platform_id: Joi.string().guid({ version: "uuidv4" }).required(),
      platform_name: Joi.string().label("Platform Name"),
      dashboard_type: Joi.array().items(dashboardTypeSchema).required(),
      platform_sub_module_array: Joi.array().items(
        Joi.object({
          sub_module_for_platform_id: Joi.string().uuid({ version: "uuidv4" }),
          sub_module_for_platform: Joi.string(),
        })
      )
    });

    // Define the schema for brand_array
    const brandSchema = Joi.object({
      brand_id: Joi.string().guid({ version: "uuidv4" }).required(),
      brand_name: Joi.string().label("Brand Name"),
      platform_array: Joi.array().items(platformSchema).required(),
      brand_sub_module_array: Joi.array().items(
        Joi.object({
          sub_module_for_brand_id: Joi.string().uuid({ version: "uuidv4" }),
          sub_module_for_brand: Joi.string(),
        })
      ),
    });

    // Define the main user schema
    const userSchema = Joi.object({
      full_name: Joi.string().min(3).required(),
      contact_number: Joi.string()
        .pattern(/^[0-9]{10}$/)
        .required(),
      email: Joi.string()
        .required()
        .email({ tlds: { allow: false } })
        .label("Email"),
      user_type: Joi.string().valid("MANAGER", "ANALYST").required(),
      organisation: Joi.string().min(1).required(),
      status: Joi.string().valid("ACTIVE", "INACTIVE", "INVITED").required(),
      brand_array: Joi.array().items(brandSchema).required(),
    });

    const validate = userSchema.validate(user);
    if (validate.error) {
      appError(validate.error);
      // appError("Please select Atleast 1 Dashboard For Platform");
      return;
    }
    saveupdatedUser(user);
  };

  // Save Changes function for Save Changes Button in Save Changes PopUp Modal
  const savechanges = () => {
    saveuser();
    setCancelToggle(false);
  };

  // Navpath for storing path on Clicking in User List, User Details in navpath & cancel button in bottom & accessing in cancelSaveChanges function
  const [navpath, setNavpath] = useState<string>("");

  // CancelToggleHandler function for Cancel Button in Save Changes Modal
  const cancelToggleHandler = (path: string) => {
    if (DataChanged && path !== "userList") {
      setNavpath("userDetailsView");
      setCancelToggle(true);
    } else if (DataChanged && path === "userList") {
      setNavpath("userList");
      setCancelToggle(true);
    } else if (path === "userDetailsView") {
      setNavpath(path);
      navigate(`/userManagement/userDetailView/${user_id}`);
    } else if (path === "userList") {
      setNavpath(path);
      navigate(`/userManagement/userList`);
    } else {
      setNavpath(path);
      navigate(`/userManagement/userDetailView/${user_id}`);
    }
  };

  // On Clicking Discard Button in SaveChanges Modal cancelSaveChanges function will be called
  const cancelSaveChanges = () => {
    if (navpath === "userDetailsView") {
      navigate(`/userManagement/userDetailView/${user_id}`);
    } else {
      navigate(`/userManagement/userList`);
    }
  };

  // Handle Change function for status
  const statushandleDropdownChange = (event: any, value: any) => {
    setUser((prev) => {
      return {
        ...prev,
        status: value,
      };
    });
    setDataChanged(true);
    // console.log("512");
  };

  // // Handle Change function for user_type
  const usertypehandleDropdownChange = (event: any, value: any) => {
    setUser((prev) => {
      return {
        ...prev,
        user_type: value,
      };
    });
    setDataChanged(true);
    // console.log("524");
  };

  // TransformAndSave Function for transforming dashboard_type_sub_module from null OR undefined to empty array
  const transformAndsave = (fetcheduser: User) => {
    const transformdata = {
      ...fetcheduser,
      brand_array: fetcheduser?.brand_array?.map((brand) => ({
        ...brand,
        brand_sub_module_array:
          brand?.brand_sub_module_array === null
            ? []
            : brand?.brand_sub_module_array,
        platform_array: brand?.platform_array?.map((platform) => ({
          ...platform,
          platform_sub_module_array:
            platform?.platform_sub_module_array === null
              ? []
              : platform?.platform_sub_module_array,
          dashboard_type: !platform?.dashboard_type
            ? []
            : platform?.dashboard_type?.map((dashboard) => ({
              ...dashboard,
              dashboard_type_sub_module:
                dashboard.dashboard_type_sub_module === null
                  ? []
                  : dashboard.dashboard_type_sub_module,
            })),
        })),
      })),
    };
    setUser(transformdata);
  };

  // FetchUserDetails function for fetching all details of User
  const fetchUserDetails = async () => {
    setDataFetchingState("LOADING");

    const token = localStorage.getItem("token");

    let api = `${BASE_URL}user/UserPlatformDashboardMapping?user_id=${user_id}`;

    const headers = {
      Authorization: `Bearer ${token}`,
    };
    try {
      const result = await axios.get(api, { headers });
      const fetchedData = result?.data?.data[0].result;

      if (!fetchedData) {
        setDataFetchingState("ERROR");
      } else {
        const isObjectEmpty = (obj: Brand): boolean => {
          return Object.values(obj).every((value) => value === null);
        };
        const { brand_array } = fetchedData;
        // Filter the brand_array
        fetchedData.brand_array = brand_array.filter(
          (brand: Brand_det) => !isObjectEmpty(brand)
        );
        transformAndsave(fetchedData);
        setDataFetchingState("SUCCESS");
      } // set the data fetching state
    } catch (error) {
      appError(error);
    } finally {
    }
  };

  // fetchbrands function checks if user_id exists Than only fetch brands by using GET API Else it Returns.
  const fetchbrands = async () => {
    if (!user_id) {
      return;
    }
    setDataFetchingState("LOADING");
    const token = localStorage.getItem("token");
    try {
      let api = `${BASE_URL}user/getBrandByUser?user_id=${user_id}`;
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const result = await axios.get(api, { headers });
      const { brand_platform_mapping } = result?.data?.data;
      if (brand_platform_mapping) {
        setBrands(brand_platform_mapping);
        setDataFetchingState("SUCCESS");
      } else {
        setDataFetchingState("ERROR");
      }
    } catch (error) {
      appError(error);
    } finally {
    }
  };

  // Variable used for showind Add Brand button in Basic Details
  const [brandlength, setBrandLength] = useState<number>(0);
  const fetchsuggestbrands = async () => {
    setDataFetchingState("LOADING");
    const token = localStorage.getItem("token");
    try {
      let api = `${BASE_URL}user/getBrandForAccess`;
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const result = await axios.get(api, { headers });
      const { brand_array } = result?.data?.data;
      // Using Hashmap
      const initialBrandMap = new Map<string, boolean>();
      brands.forEach((brand) => {
        initialBrandMap.set(brand.brand_id, true);
      });
      const filteredData: SuggestBrand[] = brand_array.filter(
        (b: Brand) => !initialBrandMap.get(b.brand_id)
      );
      setBrandLength(filteredData?.length);
      setDataFetchingState("SUCCESS");
    } catch (error) {
      appError(error);
    } finally {
    }
  };
  const [sub_modules, setSub_modules] = useState<Sub_Module[]>([]);
  const [sub_modulesForPlatform, setSub_modulesForPlatform] = useState<Sub_ModuleForPlatform[]>([]);

  const fetchSubModulesForBrand = async () => {
    const token = localStorage.getItem("token");
    try {
      let api = `${BASE_URL}user/getSubModuleForBrand`;
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const result = await axios.get(api, { headers });
      const { sub_module_for_brand } = result?.data?.data;
      if (sub_module_for_brand?.length > 0) {
        setSub_modules(sub_module_for_brand);
      }
    } catch (error) {
      appError(error);
    }
  };

  const fetchSubModulesForPlatforn = async () => {
    const token = localStorage.getItem("token");
    try {
      let api = `${BASE_URL}user/getSubModuleForPlatform`;
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const result = await axios.get(api, { headers });
      const { sub_module_for_platform } = result?.data?.data;
      if (sub_module_for_platform?.length > 0) {
        // setSub_modulesForBrand(sub_module_for_platform);
        setSub_modulesForPlatform(sub_module_for_platform);
      }
    } catch (error) {
      appError(error);
    }
  };

  const submoduleChangeHandler = (brandId: string, subModuleId: string) => {
    setUser((prev) => ({
      ...prev,
      brand_array: prev.brand_array.map((brand) =>
        brand.brand_id === brandId
          ? {
            ...brand,
            brand_sub_module_array: Array.isArray(
              brand.brand_sub_module_array
            )
              ? // If it's an array, process the sub_modules
              brand.brand_sub_module_array.some(
                (sub_module) =>
                  sub_module.sub_module_for_brand_id === subModuleId
              )
                ? // If subModuleId exists, remove it
                brand.brand_sub_module_array.filter(
                  (sub_module) =>
                    sub_module.sub_module_for_brand_id !== subModuleId
                )
                : // If subModuleId doesn't exist, find it in sub_modules and add it
                [
                  ...brand.brand_sub_module_array,
                  sub_modules.find(
                    (sub_module) =>
                      sub_module.sub_module_for_brand_id === subModuleId
                  ) || ({} as Sub_Module), // Fallback
                ]
              : // If brand_sub_module_array is not an array, initialize it as an empty array
              [
                sub_modules.find(
                  (sub_module) =>
                    sub_module.sub_module_for_brand_id === subModuleId
                ) || ({} as Sub_Module),
              ],
          }
          : brand
      ),
    }));
    setDataChanged(true);
  };

  const submoduleChangeHandlerForPlatform = (
    brandId: string,
    platformId: string,
    subModuleId: string
  ) => {
    setUser((prev) => ({
      ...prev,
      brand_array: prev.brand_array.map((brand) =>
        brand.brand_id === brandId
          ? {
            ...brand,
            platform_array: brand.platform_array.map((platform) =>
              platform.platform_id === platformId
                ? {
                  ...platform,
                  platform_sub_module_array: Array.isArray(
                    platform.platform_sub_module_array
                  )
                    ? platform.platform_sub_module_array.some(
                      (sub_module) =>
                        sub_module.sub_module_for_platform_id === subModuleId
                    )
                      ? platform.platform_sub_module_array.filter(
                        (sub_module) =>
                          sub_module.sub_module_for_platform_id !==
                          subModuleId
                      )
                      : [
                        ...platform.platform_sub_module_array,
                        sub_modulesForPlatform?.find(
                          (sub_module: Sub_ModuleForPlatform) =>
                            sub_module.sub_module_for_platform_id ===
                            subModuleId
                        ) || ({} as Sub_ModuleForPlatform),
                      ]
                    : [
                      sub_modulesForPlatform?.find(
                        (sub_module: Sub_ModuleForPlatform) =>
                          sub_module.sub_module_for_platform_id ===
                          subModuleId
                      ) || ({} as Sub_ModuleForPlatform),
                    ],
                }
                : platform
            ),
          }
          : brand
      ),
    }));
    setDataChanged(true);
  };

  // window.history.pushState({ page: 'userDetailsView' }, '', window.location.href);
  // window.addEventListener('popstate',(event: PopStateEvent) => {
  //   // cancelToggleHandler("userDetailsView");
  //   if (event.state && event.state.page === 'userDetailsView') {
  //     cancelToggleHandler("userDetailsView");
  //   }
  // })

  // UseEffect is used for calling fetchUserDetails function only on First Render
  useEffect(() => {
    fetchUserDetails();
    fetchbrands();
    fetchsuggestbrands();
    fetchSubModulesForBrand();
    fetchSubModulesForPlatforn();
  }, []);

  // ActionComponent used for Remove Brand & Add Platform
  const ActionComponent: React.FC<{
    brand_id: string;
    brand_name: string;
    platform_length: number;
  }> = ({ brand_id, brand_name, platform_length }) => {
    return (
      <>
        <Dropdown>
          <MenuButton
            slots={{ root: IconButton }}
            slotProps={{ root: { variant: "outlined", color: "neutral" } }}
            sx={{
              border: "0px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.9375 10C10.9375 10.1854 10.8825 10.3667 10.7795 10.5208C10.6765 10.675 10.5301 10.7952 10.3588 10.8661C10.1875 10.9371 9.99896 10.9557 9.8171 10.9195C9.63525 10.8833 9.4682 10.794 9.33709 10.6629C9.20598 10.5318 9.11669 10.3648 9.08051 10.1829C9.04434 10.001 9.06291 9.81254 9.13386 9.64123C9.20482 9.46993 9.32498 9.32351 9.47915 9.2205C9.63332 9.11748 9.81458 9.0625 10 9.0625C10.2486 9.0625 10.4871 9.16127 10.6629 9.33709C10.8387 9.5129 10.9375 9.75136 10.9375 10ZM10 5.625C10.1854 5.625 10.3667 5.57002 10.5208 5.467C10.675 5.36399 10.7952 5.21757 10.8661 5.04627C10.9371 4.87496 10.9557 4.68646 10.9195 4.5046C10.8833 4.32275 10.794 4.1557 10.6629 4.02459C10.5318 3.89348 10.3648 3.80419 10.1829 3.76801C10.001 3.73184 9.81254 3.75041 9.64123 3.82136C9.46993 3.89232 9.32351 4.01248 9.2205 4.16665C9.11748 4.32082 9.0625 4.50208 9.0625 4.6875C9.0625 4.93614 9.16127 5.1746 9.33709 5.35041C9.5129 5.52623 9.75136 5.625 10 5.625ZM10 14.375C9.81458 14.375 9.63332 14.43 9.47915 14.533C9.32498 14.636 9.20482 14.7824 9.13386 14.9537C9.06291 15.125 9.04434 15.3135 9.08051 15.4954C9.11669 15.6773 9.20598 15.8443 9.33709 15.9754C9.4682 16.1065 9.63525 16.1958 9.8171 16.232C9.99896 16.2682 10.1875 16.2496 10.3588 16.1786C10.5301 16.1077 10.6765 15.9875 10.7795 15.8333C10.8825 15.6792 10.9375 15.4979 10.9375 15.3125C10.9375 15.0639 10.8387 14.8254 10.6629 14.6496C10.4871 14.4738 10.2486 14.375 10 14.375Z"
                fill="#1C1C1C"
                fill-opacity="0.8"
              />
            </svg>
          </MenuButton>
          <Menu
            placement="bottom-end"
            sx={{
              border: "0px",
            }}
          >
            <MenuItem
              component="button"
              onClick={() => RemoveBrandHandler(brand_id, brand_name)}
            >
              Remove Brand
            </MenuItem>
            {platform_length > 0 && (
              <MenuItem
                component="button"
                onClick={() => addPlatformstoggle(brand_id)}
              >
                Add Platform
              </MenuItem>
            )}
          </Menu>
        </Dropdown>
      </>
    );
  };

  // const BrandComponent: React.FC<{ brand: Brand_det }> = ({ brand }) => {
  //   const [platformlength, setPlatformlength] = useState<number>(0);
  //   useEffect(() => {
  //     const fetchsuggestplatforms = async () => {
  //       const token = localStorage.getItem("token");
  //       try {
  //         let api = `${BASE_URL}user/getPlatformForAccess?brand_id=${brand?.brand_id}`;
  //         const headers = {
  //           Authorization: `Bearer ${token}`,
  //         };
  //         const result = await axios.get(api, { headers });
  //         const { platform_array } = result?.data?.data;
  //         // Using Hashmap
  //         if (brand?.platform_array.length === 0) {
  //           setPlatformlength(platform_array.length);
  //         } else {
  //           const platformIdmap = new Map<string, boolean>();
  //           brand?.platform_array.forEach((platform) => {
  //             platformIdmap.set(platform.platform_id, true);
  //           });
  //           const filteredData = platform_array.filter(
  //             (p: { platform_id: string; platform: string }) =>
  //               !platformIdmap.get(p.platform_id)
  //           );
  //           setPlatformlength(filteredData.length);
  //         }
  //       } catch (error) {
  //         appError(error);
  //       } finally {
  //       }
  //     };
  //     fetchsuggestplatforms();
  //   }, []);

  //   return (
  //     <div
  //       className="flex flex-col items-start self-stretch rounded-[16px]"
  //       style={{
  //         backgroundColor: `${WHITE.W_100}`,
  //         boxShadow: `0px 4px 10px 4px rgba(130,130,130,0.05)`,
  //       }}
  //     >
  //       <div
  //         className="flex px-[24px] py-4 items-center gap-2 self-stretch rounded-t-[16px]"
  //         style={{
  //           height: 56,
  //           // boxShadow: "0px 4px 10px 4px rgba(130,130,130,0.05)",
  //           background: "#F6F7F9",
  //         }}
  //       >
  //         <div
  //           className="flex-1 shrink-0 basis-0"
  //           style={{
  //             fontSize: 16,
  //             fontStyle: "normal",
  //             fontWeight: 700,
  //             lineHeight: "20px",
  //           }}
  //         >
  //           {brand.brand_name}
  //         </div>
  //         <ActionComponent
  //           brand_id={brand.brand_id}
  //           brand_name={brand.brand_name}
  //           platform_length={platformlength}
  //         />
  //       </div>
  //       <div className="flex flex-col items-start gap-6 self-stretch p-6">
  //         <div
  //           className="flex p-6 flex-col items-start gap-6 self-stretch rounded-[16px]"
  //           style={{ border: `1px solid ${BLACK.B_5}` }}
  //         >
  //           <div className="flex flex-col items-start gap-1 self-stretch">
  //             <div
  //               className="self-stretch"
  //               style={{
  //                 color: `${BLACK.B_100}`,
  //                 fontSize: 16,
  //                 fontWeight: 700,
  //                 lineHeight: "20px",
  //               }}
  //             >
  //               Select Brand Access
  //             </div>
  //             <div
  //               className="self-stretch"
  //               style={{
  //                 color: `${BLACK.B_40}`,
  //                 fontSize: 12,
  //                 fontWeight: 400,
  //                 lineHeight: "18px",
  //               }}
  //             >
  //               Choose access permissions based on your brand
  //             </div>
  //           </div>
  //           <div className="flex items-start gap-10">
  //             <label className="flex items-center gap-2">
  //               <input
  //                 type="checkbox"
  //                 name="options"
  //                 value="keywordmapping"
  //                 checked
  //                 style={{
  //                   width: 16,
  //                   height: 16,
  //                   accentColor: "black",
  //                 }}
  //               />
  //               Keyword Mapping
  //             </label>
  //             <label className="flex items-center gap-2">
  //               <input
  //                 type="checkbox"
  //                 name="options"
  //                 value="crossplatformmapping"
  //                 checked
  //                 style={{
  //                   width: 16,
  //                   height: 16,
  //                   accentColor: "black",
  //                 }}
  //               />
  //               Cross Platform Mapping
  //             </label>
  //             <label className="flex items-center gap-2">
  //               <input
  //                 type="checkbox"
  //                 name="options"
  //                 value="Favouritegroups"
  //                 checked
  //                 style={{
  //                   width: 16,
  //                   height: 16,
  //                   accentColor: "black",
  //                 }}
  //               />
  //               Favourite Groups
  //             </label>
  //             <label className="flex items-center gap-2">
  //               <input
  //                 type="checkbox"
  //                 name="options"
  //                 value="keywordmapping"
  //                 checked
  //                 style={{
  //                   width: 16,
  //                   height: 16,
  //                   accentColor: "black",
  //                 }}
  //               />
  //               Unmapped Data
  //             </label>
  //           </div>
  //         </div>
  //         <div className="flex items-start gap-6 self-stretch">
  //           {brand?.platform_array &&
  //             brand?.platform_array?.length > 0 &&
  //             brand?.platform_array?.map((platform) => {
  //               return (
  //                 <PlatformComponent
  //                   user={user}
  //                   brand={brand}
  //                   platform={platform}
  //                 />
  //               );
  //             })}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  // const PlatformComponent: React.FC<{
  //   user: User;
  //   brand: Brand_det;
  //   platform: Platform;
  // }> = ({ user, brand, platform }) => {
  //   const [Dashlength, setDashLength] = useState<number>(0);
  //   useEffect(() => {
  //     const fetchsuggestdashboards = async () => {
  //       const token = localStorage.getItem("token");
  //       try {
  //         let api = `${BASE_URL}user/getDashboardTypeForAccess?brand_id=${brand?.brand_id}&platform_id=${platform?.platform_id}`;
  //         const headers = {
  //           Authorization: `Bearer ${token}`,
  //         };
  //         const result = await axios.get(api, { headers });
  //         const { dashboard_array } = result?.data?.data;
  //         if (dashboard_array === 0) {
  //           setDashLength(dashboard_array?.length);
  //         } else {
  //           const platformIdmap = new Map<string, boolean>();
  //           platform?.dashboard_type.forEach((dashboard) => {
  //             platformIdmap.set(dashboard.dashboard_type_id, true);
  //           });
  //           const filteredData = dashboard_array.filter(
  //             (d: SuggestedDashboard) =>
  //               !platformIdmap.get(d.brand_power_bi_dashboard_type_id)
  //           );
  //           setDashLength(filteredData.length);
  //         }
  //       } catch (error) {
  //         appError(error);
  //       } finally {
  //       }
  //     };
  //     fetchsuggestdashboards();
  //   }, []);
  //   return (
  //     <div
  //       className="flex p-6 flex-col items-start gap-[32px] self-stretch rounded-[16px]"
  //       style={{
  //         border: `1px solid ${BLACK.B_5}`,
  //         width: "49%",
  //       }}
  //     >
  //       {/* Div for Platform Heading & Remove Button */}
  //       <div className="flex justify-between items-center self-stretch">
  //         <div className="">{platform?.platform_name}</div>
  //         <div className="flex justify-center items-center gap-[8px] rounded-[8px]">
  //           <div
  //             className="underline cursor-pointer"
  //             style={{
  //               fontSize: 14,
  //               fontStyle: "normal",
  //               fontWeight: 500,
  //               lineHeight: "20px",
  //               color: STATUS.S_Red,
  //             }}
  //             onClick={() =>
  //               RemovePlatformHandler(
  //                 brand.brand_id,
  //                 platform.platform_name,
  //                 platform.platform_id
  //               )
  //             }
  //           >
  //             Remove
  //           </div>
  //         </div>
  //       </div>
  //       {/* subdiv1 : For Select Perform Access */}
  //       <div className="flex flex-col self-start gap-6 self-stretch">
  //         <div className="flex flex-col items-start gap-[8px] self-stretch">
  //           <div className="flex flex-col items-start self-stretch">
  //             <div
  //               className=""
  //               style={{
  //                 fontSize: 16,
  //                 fontWeight: 700,
  //                 fontStyle: "normal",
  //                 color: "#1C1C1C",
  //               }}
  //             >
  //               Select Platform Access
  //             </div>
  //             <div
  //               className=""
  //               style={{
  //                 fontSize: 12,
  //                 fontWeight: 400,
  //                 fontStyle: "normal",
  //                 color: `${BLACK.B_40}`,
  //                 lineHeight: "18px",
  //               }}
  //             >
  //               Choose access permissions based on your platform
  //             </div>
  //           </div>
  //           <div className="flex gap-10 items-start">
  //             <label
  //               className="flex items-center gap-2"
  //               style={{
  //                 fontSize: 14,
  //                 fontWeight: 400,
  //                 fontStyle: "normal",
  //                 color: "#1C1C1C",
  //               }}
  //             >
  //               <input
  //                 type="checkbox"
  //                 name="options"
  //                 value="keywordmapping"
  //                 checked
  //                 style={{
  //                   width: 16,
  //                   height: 16,
  //                   accentColor: "black",
  //                 }}
  //               />
  //               Favourite Groups
  //             </label>
  //           </div>
  //         </div>
  //       </div>

  //       {/* div for border */}
  //       {platform?.dashboard_type?.length !== 0 && (
  //         <div
  //           className=""
  //           style={{
  //             border: `1px solid ${BLACK.B_10}`,
  //             width: "100%",
  //           }}
  //         ></div>
  //       )}

  //       {/* subdiv2 : Parent Div of All Dashboards */}
  //       <div className="gap-10 flex flex-col items-start self-stretch">
  //         {/* Ads_Dashboard Subsection */}
  //         {platform?.dashboard_type?.length > 0 ? (
  //           platform?.dashboard_type?.map((dashboard, i) => {
  //             return (
  //               // Single Dashboard Div
  //               <div
  //                 className="flex flex-col items-start gap-4 self-stretch"
  //                 key={i}
  //               >
  //                 <div
  //                   className="flex justify-center items-center gap-3 py-1 pl-3"
  //                   style={{
  //                     borderLeft: `4px solid ${dashboard.color}`,
  //                   }}
  //                 >
  //                   <div
  //                     className=""
  //                     style={{
  //                       fontSize: 16,
  //                       fontWeight: 700,
  //                       fontStyle: "normal",
  //                       color: "#1C1C1C",
  //                       lineHeight: "24px",
  //                     }}
  //                   >
  //                     {`${dashboard.dashboard_type_name}`}
  //                   </div>
  //                   <div className="flex justify-center items-center gap-[8px] rounded-[8px] cursor-pointer">
  //                     <div
  //                       className="underline"
  //                       style={{
  //                         fontSize: 14,
  //                         fontStyle: "normal",
  //                         fontWeight: 500,
  //                         lineHeight: "20px",
  //                         color: STATUS.S_Red,
  //                       }}
  //                       onClick={() =>
  //                         RemoveDashboardHandler(
  //                           brand.brand_id,
  //                           dashboard.dashboard_type_name,
  //                           platform.platform_id,
  //                           dashboard.dashboard_type_id
  //                         )
  //                       }
  //                     >
  //                       Remove
  //                     </div>
  //                   </div>
  //                 </div>

  //                 {dashboard?.dashboard_type_id && (
  //                   <SubDashboardsForEdit
  //                     dashboard_id={dashboard.dashboard_type_id}
  //                     userDashboards={dashboard.dashboard_type_sub_module}
  //                     user={user}
  //                     brand_id={brand.brand_id}
  //                     platform_id={platform.platform_id}
  //                     setUser={setUser}
  //                     setDataChanged={setDataChanged}
  //                   />
  //                 )}

  //                 {i === platform?.dashboard_type?.length - 1 &&
  //                   Dashlength > 0 && (
  //                     <div
  //                       className="flex h-[40px] justify-center items-center gap-2 rounded-[8px] cursor-pointer "
  //                       onClick={() =>
  //                         addDashboardtoggle(
  //                           brand.brand_id,
  //                           platform.platform_id
  //                         )
  //                       }
  //                     >
  //                       <div className="w-[16px] h-[16px]">
  //                         <svg
  //                           xmlns="http://www.w3.org/2000/svg"
  //                           width="16"
  //                           height="16"
  //                           viewBox="0 0 16 16"
  //                           fill="none"
  //                         >
  //                           <path
  //                             d="M7.33325 8.66671H3.99992C3.81103 8.66671 3.6527 8.60282 3.52492 8.47504C3.39714 8.34726 3.33325 8.18893 3.33325 8.00004C3.33325 7.81115 3.39714 7.65282 3.52492 7.52504C3.6527 7.39726 3.81103 7.33337 3.99992 7.33337H7.33325V4.00004C7.33325 3.81115 7.39714 3.65282 7.52492 3.52504C7.6527 3.39726 7.81103 3.33337 7.99992 3.33337C8.18881 3.33337 8.34714 3.39726 8.47492 3.52504C8.6027 3.65282 8.66659 3.81115 8.66659 4.00004V7.33337H11.9999C12.1888 7.33337 12.3471 7.39726 12.4749 7.52504C12.6027 7.65282 12.6666 7.81115 12.6666 8.00004C12.6666 8.18893 12.6027 8.34726 12.4749 8.47504C12.3471 8.60282 12.1888 8.66671 11.9999 8.66671H8.66659V12C8.66659 12.1889 8.6027 12.3473 8.47492 12.475C8.34714 12.6028 8.18881 12.6667 7.99992 12.6667C7.81103 12.6667 7.6527 12.6028 7.52492 12.475C7.39714 12.3473 7.33325 12.1889 7.33325 12V8.66671Z"
  //                             fill="#1886FE"
  //                           />
  //                         </svg>
  //                       </div>
  //                       <div
  //                         className="underline"
  //                         style={{
  //                           fontSize: "14px",
  //                           fontStyle: "normal",
  //                           fontWeight: 500,
  //                           lineHeight: "20px",
  //                           color: PRIMARY.P_BLue,
  //                         }}
  //                       >
  //                         Add Dashboard
  //                       </div>
  //                     </div>
  //                   )}
  //               </div>
  //             );
  //           })
  //         ) : (
  //           <div
  //             className="flex h-[40px] justify-center items-center gap-2 rounded-[8px] cursor-pointer"
  //             onClick={() =>
  //               addDashboardtoggle(brand.brand_id, platform.platform_id)
  //             }
  //           >
  //             <div className="w-[16px] h-[16px]">
  //               <svg
  //                 xmlns="http://www.w3.org/2000/svg"
  //                 width="16"
  //                 height="16"
  //                 viewBox="0 0 16 16"
  //                 fill="none"
  //               >
  //                 <path
  //                   d="M7.33325 8.66671H3.99992C3.81103 8.66671 3.6527 8.60282 3.52492 8.47504C3.39714 8.34726 3.33325 8.18893 3.33325 8.00004C3.33325 7.81115 3.39714 7.65282 3.52492 7.52504C3.6527 7.39726 3.81103 7.33337 3.99992 7.33337H7.33325V4.00004C7.33325 3.81115 7.39714 3.65282 7.52492 3.52504C7.6527 3.39726 7.81103 3.33337 7.99992 3.33337C8.18881 3.33337 8.34714 3.39726 8.47492 3.52504C8.6027 3.65282 8.66659 3.81115 8.66659 4.00004V7.33337H11.9999C12.1888 7.33337 12.3471 7.39726 12.4749 7.52504C12.6027 7.65282 12.6666 7.81115 12.6666 8.00004C12.6666 8.18893 12.6027 8.34726 12.4749 8.47504C12.3471 8.60282 12.1888 8.66671 11.9999 8.66671H8.66659V12C8.66659 12.1889 8.6027 12.3473 8.47492 12.475C8.34714 12.6028 8.18881 12.6667 7.99992 12.6667C7.81103 12.6667 7.6527 12.6028 7.52492 12.475C7.39714 12.3473 7.33325 12.1889 7.33325 12V8.66671Z"
  //                   fill="#1886FE"
  //                 />
  //               </svg>
  //             </div>
  //             <div
  //               className="underline"
  //               style={{
  //                 fontSize: "14px",
  //                 fontStyle: "normal",
  //                 fontWeight: 500,
  //                 lineHeight: "20px",
  //                 color: PRIMARY.P_BLue,
  //               }}
  //             >
  //               Add Dashboard
  //             </div>
  //           </div>
  //         )}
  //       </div>
  //     </div>
  //   );
  // };

  // Amazon, Flipkart, Zepto, Swiggy Instamart, Myntra, Blinkit
  const mastersAccessForPlatforms = [
    // "2b2c5687-6887-4f4c-9509-4abd7341b81e", "76cfb8ab-1616-4342-a43f-b5c22ff50201",
    // "bcc0f4b5-362b-40c8-9546-b3692b5cb7d2", "84578a57-6df7-4b78-99ed-82127aab3509", 
    // "c8cd22e7-d593-41d6-b9c3-9bbbb2021ebe", "af927f03-72a9-4cfc-bdd2-fae9a333a4e2"
    "e19cd888-4857-4c97-aadf-81552edbb121"];
  return (
    <>
      {dataFetchingState === "SUCCESS" && (
        <div
          className="flex flex-col gap-6 pt-[11px]"
          style={{
            height: "calc(100vh - 72px)",
            overflow: "scroll",
            scrollbarWidth: "none",
          }}
        >
          {/* This is User List > User Details > Edit Details Sub Component */}
          <div className="flex w-fit justify-between items-center pt-4 mx-10 h-10">
            <div className="flex flex-row items-center">
              <span
                // onClick={() => navigate("/userManagement/userList")}
                onClick={() => cancelToggleHandler("userList")}
                className="cursor-pointer"
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: BLACK.B_40,
                }}
              >
                User List
              </span>
              <span className="mx-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8.40003 7.99999L5.80003 5.39999C5.67781 5.27777 5.6167 5.12221 5.6167 4.93333C5.6167 4.74444 5.67781 4.58888 5.80003 4.46666C5.92225 4.34444 6.07781 4.28333 6.2667 4.28333C6.45559 4.28333 6.61114 4.34444 6.73337 4.46666L9.80003 7.53333C9.8667 7.59999 9.91392 7.67221 9.9417 7.74999C9.96948 7.82777 9.98337 7.9111 9.98337 7.99999C9.98337 8.08888 9.96948 8.17221 9.9417 8.24999C9.91392 8.32777 9.8667 8.39999 9.80003 8.46666L6.73337 11.5333C6.61114 11.6555 6.45559 11.7167 6.2667 11.7167C6.07781 11.7167 5.92225 11.6555 5.80003 11.5333C5.67781 11.4111 5.6167 11.2555 5.6167 11.0667C5.6167 10.8778 5.67781 10.7222 5.80003 10.6L8.40003 7.99999Z"
                    fill="#1C1C1C"
                    fill-opacity="0.4"
                  />
                </svg>
              </span>
              <span
                // onClick={() => navigate(`/userManagement/userDetailView/${user_id}`)}
                onClick={() => cancelToggleHandler("userDetailsView")}
                className="cursor-pointer"
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: "18px",
                  color: BLACK.B_40,
                }}
              >
                User Details
              </span>
              <span className="mx-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8.40003 7.99999L5.80003 5.39999C5.67781 5.27777 5.6167 5.12221 5.6167 4.93333C5.6167 4.74444 5.67781 4.58888 5.80003 4.46666C5.92225 4.34444 6.07781 4.28333 6.2667 4.28333C6.45559 4.28333 6.61114 4.34444 6.73337 4.46666L9.80003 7.53333C9.8667 7.59999 9.91392 7.67221 9.9417 7.74999C9.96948 7.82777 9.98337 7.9111 9.98337 7.99999C9.98337 8.08888 9.96948 8.17221 9.9417 8.24999C9.91392 8.32777 9.8667 8.39999 9.80003 8.46666L6.73337 11.5333C6.61114 11.6555 6.45559 11.7167 6.2667 11.7167C6.07781 11.7167 5.92225 11.6555 5.80003 11.5333C5.67781 11.4111 5.6167 11.2555 5.6167 11.0667C5.6167 10.8778 5.67781 10.7222 5.80003 10.6L8.40003 7.99999Z"
                    fill="#1C1C1C"
                    fill-opacity="0.4"
                  />
                </svg>
              </span>
              <span
                style={{ fontSize: 12, fontWeight: 500, lineHeight: "18px" }}
              >
                Edit Details
              </span>
            </div>
          </div>

          <div className="flex flex-col items-start gap-6 mx-10">
            {/* Sub Div 1 : Basic Details of User name, type, email, etc. */}
            <div
              className="flex p-6 flex-col items-start gap-6 self-stretch rounded-[16px] "
              style={{
                border: `1px solid ${BLACK.B_5}`,
                backgroundColor: `${WHITE.W_100}`,
                boxShadow: "0px 4px 10px 4px rgba(130,130,130,0.05)",
              }}
            >
              {/* Basic Details Div */}
              <div className="flex flex-col items-start gap-1 self-stretch">
                <div
                  className="self-stretch"
                  style={{
                    color: `${BLACK.B_100}`,
                    fontSize: 16,
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "20px",
                  }}
                >
                  Basic Details
                </div>
                <div
                  className="self-stretch"
                  style={{
                    color: `${BLACK.B_40}`,
                    fontSize: 12,
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "18px",
                  }}
                >
                  Review and adjust the basic information as necessary
                </div>
              </div>

              {/* First 3 Fields Name, Status, User Type */}
              <div className="flex flex-col items-start gap-6 self-stretch">
                <div className="flex items-center gap-[60px] self-stretch">
                  <div className="flex flex-col items-start gap-1 w-[30%]">
                    <div
                      className="flex h-[20px] flex-col justify-center self-stretch w-full"
                      style={{
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "20px",
                        color: "#5B5B5B",
                      }}
                    >
                      Name
                    </div>
                    <Input
                      placeholder="User Name"
                      className="w-full"
                      value={user?.full_name}
                      sx={{
                        "--Input-focusedThickness": "0px",

                        height: "52px",
                        border: `1px solid ${BLACK.B_5}`,
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "20px",
                        backgroundColor: WHITE.W_100,
                        boxShadow: "none",
                      }}
                      onChange={(e) => handleChange(e, "full_name")}
                    />
                  </div>

                  {user?.status && (
                    <div className="flex flex-col items-start gap-1 w-[30%]">
                      <div
                        className="flex h-[20px] flex-col justify-center self-stretch w-full"
                        style={{
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontWeight: 500,
                          lineHeight: "20px",
                          color: "#5B5B5B",
                        }}
                      >
                        Status
                      </div>
                      <Select
                        placeholder="Select Status"
                        className="w-full"
                        onChange={statushandleDropdownChange}
                        // defaultValue={user?.status}
                        name="status"
                        value={user?.status}
                        indicator={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M9.99984 12.4791C9.88873 12.4791 9.78456 12.4618 9.68734 12.4271C9.59012 12.3923 9.49984 12.3333 9.4165 12.25L5.58317 8.41665C5.43039 8.26387 5.354 8.06942 5.354 7.83331C5.354 7.5972 5.43039 7.40276 5.58317 7.24998C5.73595 7.0972 5.93039 7.02081 6.1665 7.02081C6.40262 7.02081 6.59706 7.0972 6.74984 7.24998L9.99984 10.5L13.2498 7.24998C13.4026 7.0972 13.5971 7.02081 13.8332 7.02081C14.0693 7.02081 14.2637 7.0972 14.4165 7.24998C14.5693 7.40276 14.6457 7.5972 14.6457 7.83331C14.6457 8.06942 14.5693 8.26387 14.4165 8.41665L10.5832 12.25C10.4998 12.3333 10.4096 12.3923 10.3123 12.4271C10.2151 12.4618 10.1109 12.4791 9.99984 12.4791Z"
                              fill="#1C1C1C"
                              fill-opacity="0.4"
                            />
                          </svg>
                        }
                        slotProps={{
                          listbox: {},
                        }}
                        sx={{
                          "--Input-focusedThickness": "0px",

                          height: "52px",
                          color: BLACK.B_80,
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "20px",
                          backgroundColor: WHITE.W_100,
                          boxShadow: "none",
                          border: `1px solid ${BLACK.B_5}`,
                          [`& .${selectClasses.indicator}`]: {
                            transition: "0.2s",
                            [`&.${selectClasses.expanded}`]: {
                              transform: "rotate(-180deg)",
                            },
                          },
                          ":hover": {
                            backgroundColor: WHITE.W_100,
                          },
                        }}
                      >
                        {user.status === "INVITED" && (
                          <Option value={"INVITED"}>Invited</Option>
                        )}
                        {user.status !== "INVITED" && (
                          <Option value={"ACTIVE"}>Active</Option>
                        )}
                        <Option value={"INACTIVE"}>Inactive</Option>
                      </Select>
                    </div>
                  )}

                  {user?.user_type && (
                    <div className="flex flex-col items-start gap-1 w-[30%]">
                      <div
                        className="flex h-[20px] flex-col justify-center self-stretch"
                        style={{
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontWeight: 500,
                          lineHeight: "20px",
                          color: "#5B5B5B",
                        }}
                      >
                        User Type
                      </div>
                      <Select
                        // defaultValue={user?.user_type}
                        name="user_type"
                        className="w-full"
                        onChange={usertypehandleDropdownChange}
                        value={user?.user_type}
                        indicator={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M9.99984 12.4791C9.88873 12.4791 9.78456 12.4618 9.68734 12.4271C9.59012 12.3923 9.49984 12.3333 9.4165 12.25L5.58317 8.41665C5.43039 8.26387 5.354 8.06942 5.354 7.83331C5.354 7.5972 5.43039 7.40276 5.58317 7.24998C5.73595 7.0972 5.93039 7.02081 6.1665 7.02081C6.40262 7.02081 6.59706 7.0972 6.74984 7.24998L9.99984 10.5L13.2498 7.24998C13.4026 7.0972 13.5971 7.02081 13.8332 7.02081C14.0693 7.02081 14.2637 7.0972 14.4165 7.24998C14.5693 7.40276 14.6457 7.5972 14.6457 7.83331C14.6457 8.06942 14.5693 8.26387 14.4165 8.41665L10.5832 12.25C10.4998 12.3333 10.4096 12.3923 10.3123 12.4271C10.2151 12.4618 10.1109 12.4791 9.99984 12.4791Z"
                              fill="#1C1C1C"
                              fill-opacity="0.4"
                            />
                          </svg>
                        }
                        slotProps={{
                          listbox: {},
                        }}
                        sx={{
                          "--Input-focusedThickness": "0px",

                          height: "52px",
                          color: BLACK.B_80,
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "20px",
                          backgroundColor: WHITE.W_100,
                          boxShadow: "none",
                          border: `1px solid ${BLACK.B_5}`,
                          [`& .${selectClasses.indicator}`]: {
                            transition: "0.2s",
                            [`&.${selectClasses.expanded}`]: {
                              transform: "rotate(-180deg)",
                            },
                          },
                          ":hover": {
                            backgroundColor: WHITE.W_100,
                          },
                        }}
                      >
                        <Option value={"ADMIN"}>Admin</Option>
                        <Option value={"MANAGER"}>Manager</Option>
                        <Option value={"ANALYST"}>Analyst</Option>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-[60px] self-stretch">
                  <div className="flex flex-col items-start gap-1 w-[30%]">
                    <div
                      className="flex h-[20px] flex-col justify-center self-stretch w-full"
                      style={{
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "20px",
                        color: "#5B5B5B",
                      }}
                    >
                      Email
                    </div>
                    <Input
                      placeholder="User Email"
                      className="w-full"
                      value={user?.email}
                      sx={{
                        "--Input-focusedThickness": "0px",

                        height: "52px",
                        border: `1px solid ${BLACK.B_5}`,
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "20px",
                        backgroundColor: WHITE.W_100,
                        boxShadow: "none",
                      }}
                      name="email"
                      onChange={(e) => handleChange(e, "email")}
                    />
                  </div>
                  <div className="flex flex-col items-start gap-1 w-[30%]">
                    <div
                      className="flex h-[20px] flex-col justify-center self-stretch w-full"
                      style={{
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "20px",
                        color: "#5B5B5B",
                      }}
                    >
                      Mobile No.
                    </div>
                    <Input
                      placeholder="User Mobile"
                      className="w-full"
                      value={user.contact_number}
                      sx={{
                        "--Input-focusedThickness": "0px",

                        height: "52px",
                        border: `1px solid ${BLACK.B_5}`,
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "20px",
                        backgroundColor: WHITE.W_100,
                        boxShadow: "none",
                      }}
                      name="contact_number"
                      onChange={(e) => handleChange(e, "contact_number")}
                    />
                  </div>
                  <div className="flex flex-col items-start gap-1 w-[30%]">
                    <div
                      className="flex h-[20px] flex-col justify-center self-stretch"
                      style={{
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "20px",
                        color: "#5B5B5B",
                      }}
                    >
                      Organisation
                    </div>
                    <Input
                      placeholder="Organisation"
                      className="w-full"
                      value={user.organisation}
                      sx={{
                        "--Input-focusedThickness": "0px",

                        height: "52px",
                        border: `1px solid ${BLACK.B_5}`,
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "20px",
                        backgroundColor: WHITE.W_100,
                        boxShadow: "none",
                      }}
                      name="contact_number"
                      onChange={(e) => handleChange(e, "organisation")}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-start gap-[8px] self-stretch">
                <div
                  className="self-stretch"
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "#5B5B5B",
                  }}
                >
                  Brands Assigned
                </div>
                <div className="flex flex-wrap gap-2">
                  {brands &&
                    brands?.length > 0 &&
                    brands?.map((brand, index) => {
                      // let marginLeft = index === 0 ? 0 : 8;
                      return (
                        <div
                          key={brand?.brand_id}
                          className="flex flex-row items-center px-2  w-fit"
                          style={{
                            height: 28,
                            borderRadius: 4,
                            backgroundColor: BLACK.B_5,
                            //   marginLeft,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              lineHeight: "18px",
                            }}
                          >
                            {brand?.brand_name}
                          </span>
                          <span
                            onClick={() =>
                              RemoveBrandHandler(
                                brand?.brand_id,
                                brand?.brand_name
                              )
                            }
                            className="ml-2 cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M12.9422 7.94219L10.8836 10L12.9422 12.0578C13.0003 12.1159 13.0463 12.1848 13.0777 12.2607C13.1092 12.3366 13.1254 12.4179 13.1254 12.5C13.1254 12.5821 13.1092 12.6634 13.0777 12.7393C13.0463 12.8152 13.0003 12.8841 12.9422 12.9422C12.8841 13.0003 12.8152 13.0463 12.7393 13.0777C12.6634 13.1092 12.5821 13.1253 12.5 13.1253C12.4179 13.1253 12.3366 13.1092 12.2607 13.0777C12.1848 13.0463 12.1159 13.0003 12.0578 12.9422L10 10.8836L7.94219 12.9422C7.88412 13.0003 7.81518 13.0463 7.73931 13.0777C7.66344 13.1092 7.58213 13.1253 7.5 13.1253C7.41788 13.1253 7.33656 13.1092 7.26069 13.0777C7.18482 13.0463 7.11588 13.0003 7.05782 12.9422C6.99975 12.8841 6.95368 12.8152 6.92226 12.7393C6.89083 12.6634 6.87466 12.5821 6.87466 12.5C6.87466 12.4179 6.89083 12.3366 6.92226 12.2607C6.95368 12.1848 6.99975 12.1159 7.05782 12.0578L9.11641 10L7.05782 7.94219C6.94054 7.82491 6.87466 7.66585 6.87466 7.5C6.87466 7.33415 6.94054 7.17509 7.05782 7.05781C7.17509 6.94054 7.33415 6.87465 7.5 6.87465C7.66586 6.87465 7.82492 6.94054 7.94219 7.05781L10 9.11641L12.0578 7.05781C12.1159 6.99974 12.1848 6.95368 12.2607 6.92225C12.3366 6.89083 12.4179 6.87465 12.5 6.87465C12.5821 6.87465 12.6634 6.89083 12.7393 6.92225C12.8152 6.95368 12.8841 6.99974 12.9422 7.05781C13.0003 7.11588 13.0463 7.18482 13.0777 7.26069C13.1092 7.33656 13.1254 7.41788 13.1254 7.5C13.1254 7.58212 13.1092 7.66344 13.0777 7.73931C13.0463 7.81518 13.0003 7.88412 12.9422 7.94219ZM18.125 10C18.125 11.607 17.6485 13.1779 16.7557 14.514C15.8629 15.8502 14.594 16.8916 13.1093 17.5065C11.6247 18.1215 9.99099 18.2824 8.4149 17.9689C6.8388 17.6554 5.39106 16.8815 4.25476 15.7452C3.11846 14.6089 2.34463 13.1612 2.03112 11.5851C1.71762 10.009 1.87852 8.37535 2.49348 6.8907C3.10844 5.40605 4.14985 4.1371 5.486 3.24431C6.82214 2.35152 8.39303 1.875 10 1.875C12.1542 1.87727 14.2195 2.73403 15.7427 4.25727C17.266 5.78051 18.1227 7.84581 18.125 10ZM16.875 10C16.875 8.64025 16.4718 7.31104 15.7164 6.18045C14.9609 5.04987 13.8872 4.16868 12.631 3.64833C11.3747 3.12798 9.99238 2.99183 8.65876 3.2571C7.32514 3.52237 6.10013 4.17716 5.13864 5.13864C4.17716 6.10013 3.52238 7.32513 3.2571 8.65875C2.99183 9.99237 3.12798 11.3747 3.64833 12.6309C4.16868 13.8872 5.04987 14.9609 6.18046 15.7164C7.31105 16.4718 8.64026 16.875 10 16.875C11.8227 16.8729 13.5702 16.1479 14.8591 14.8591C16.1479 13.5702 16.8729 11.8227 16.875 10Z"
                                fill="#1C1C1C"
                                fill-opacity="0.4"
                              />
                            </svg>
                          </span>
                        </div>
                      );
                    })}
                </div>
                {brandlength > 0 && (
                  <div
                    onClick={() => addBrandstoggle()}
                    className="flex items-center cursor-pointer w-fit"
                    style={{ marginTop: 14 }}
                  >
                    <span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M7.33325 8.66665H3.99992C3.81103 8.66665 3.6527 8.60276 3.52492 8.47498C3.39714 8.3472 3.33325 8.18887 3.33325 7.99998C3.33325 7.81109 3.39714 7.65276 3.52492 7.52498C3.6527 7.3972 3.81103 7.33331 3.99992 7.33331H7.33325V3.99998C7.33325 3.81109 7.39714 3.65276 7.52492 3.52498C7.6527 3.3972 7.81103 3.33331 7.99992 3.33331C8.18881 3.33331 8.34714 3.3972 8.47492 3.52498C8.6027 3.65276 8.66659 3.81109 8.66659 3.99998V7.33331H11.9999C12.1888 7.33331 12.3471 7.3972 12.4749 7.52498C12.6027 7.65276 12.6666 7.81109 12.6666 7.99998C12.6666 8.18887 12.6027 8.3472 12.4749 8.47498C12.3471 8.60276 12.1888 8.66665 11.9999 8.66665H8.66659V12C8.66659 12.1889 8.6027 12.3472 8.47492 12.475C8.34714 12.6028 8.18881 12.6666 7.99992 12.6666C7.81103 12.6666 7.6527 12.6028 7.52492 12.475C7.39714 12.3472 7.33325 12.1889 7.33325 12V8.66665Z"
                          fill="#1886FE"
                        />
                      </svg>
                    </span>
                    <span
                      // onClick={addBrandstoggle}
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: "20px",
                        textDecoration: "underline",
                        color: PRIMARY.P_BLue,
                      }}
                    >
                      Add Brands
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Sub Div 2 : Single Brand All Details */}
            {user.brand_array &&
              user.brand_array?.length > 0 &&
              user.brand_array?.map((brand) => {
                return (
                  // Error component
                  // <BrandComponent
                  //   brand={brand}
                  //   submoduleChangeHandler={submoduleChangeHandler}
                  //   key={brand?.brand_id}
                  // />

                  //Live Production Code
                  <div
                    className="flex flex-col items-start self-stretch rounded-[16px]"
                    style={{
                      backgroundColor: `${WHITE.W_100}`,
                      boxShadow: `0px 4px 10px 4px rgba(130,130,130,0.05)`,
                    }}
                  >
                    <div
                      className="flex px-[24px] py-4 items-center gap-2 self-stretch rounded-t-[16px]"
                      style={{
                        height: 56,
                        // boxShadow: "0px 4px 10px 4px rgba(130,130,130,0.05)",
                        background: "#F6F7F9",
                      }}
                    >
                      <div
                        className="flex-1 shrink-0 basis-0"
                        style={{
                          fontSize: 16,
                          fontStyle: "normal",
                          fontWeight: 700,
                          lineHeight: "20px",
                        }}
                      >
                        {brand.brand_name}
                      </div>
                      <ActionComponent
                        brand_id={brand.brand_id}
                        brand_name={brand.brand_name}
                        platform_length={brand.platform_array?.length}
                      />
                    </div>
                    <div className="flex flex-col items-start gap-6 self-stretch p-6">
                      <div
                        className="flex p-6 flex-col items-start gap-6 self-stretch rounded-[16px]"
                        style={{ border: `1px solid ${BLACK.B_5}` }}
                      >
                        <div className="flex flex-col items-start gap-1 self-stretch">
                          <div
                            className="self-stretch"
                            style={{
                              color: `${BLACK.B_100}`,
                              fontSize: 16,
                              fontWeight: 700,
                              lineHeight: "20px",
                            }}
                          >
                            Select Brand Access
                          </div>
                          <div
                            className="self-stretch"
                            style={{
                              color: `${BLACK.B_40}`,
                              fontSize: 12,
                              fontWeight: 400,
                              lineHeight: "18px",
                            }}
                          >
                            Choose access permissions based on your brand
                          </div>
                        </div>
                        <div className="grid grid-cols-10 gap-x-10 gap-y-4">
                          {sub_modules?.map((sub_module: Sub_Module) => (
                            <label
                              className="flex items-center gap-2"
                              key={sub_module?.sub_module_for_brand_id}
                            >
                              <input
                                type="checkbox"
                                name="options"
                                value="keywordmapping"
                                checked={brand?.brand_sub_module_array?.some(
                                  (subModule) =>
                                    subModule?.sub_module_for_brand_id ===
                                    sub_module?.sub_module_for_brand_id
                                )}
                                style={{
                                  width: 16,
                                  height: 16,
                                  accentColor: "black",
                                  cursor: "pointer",
                                }}
                                onChange={() =>
                                  submoduleChangeHandler(
                                    brand?.brand_id,
                                    sub_module?.sub_module_for_brand_id
                                  )
                                }
                              />
                              {sub_module?.sub_module_for_brand}
                            </label>
                          ))}
                          {/* <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              name="options"
                              value="keywordmapping"
                              checked
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: "black",
                              }}
                            />
                            Keyword Mapping
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              name="options"
                              value="crossplatformmapping"
                              checked
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: "black",
                              }}
                            />
                            Cross Platform Mapping
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              name="options"
                              value="Favouritegroups"
                              checked
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: "black",
                              }}
                            />
                            Favourite Groups
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              name="options"
                              value="keywordmapping"
                              checked
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: "black",
                              }}
                            />
                            Unmapped Data
                          </label> */}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-start gap-6 self-stretch">
                        {brand?.platform_array &&
                          brand?.platform_array?.length > 0 &&
                          brand?.platform_array?.map((platform) => {
                            return (
                              // <PlatformComponent
                              //   user={user}
                              //   brand={brand}
                              //   platform={platform}
                              // />

                              <div
                                className="flex p-6 flex-col items-start gap-[32px] self-stretch rounded-[16px]"
                                style={{
                                  border: `1px solid ${BLACK.B_5}`,
                                  width: "49%",
                                }}
                              >
                                {/* Div for Platform Heading & Remove Button */}
                                <div className="flex justify-between items-center self-stretch">
                                  <div className="">
                                    {platform?.platform_name}
                                  </div>
                                  <div className="flex justify-center items-center gap-[8px] rounded-[8px]">
                                    <div
                                      className="underline cursor-pointer"
                                      style={{
                                        fontSize: 14,
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "20px",
                                        color: STATUS.S_Red,
                                      }}
                                      onClick={() => {
                                        if (brand?.platform_array?.length > 1) {
                                          RemovePlatformHandler(
                                            brand.brand_id,
                                            platform.platform_name,
                                            platform.platform_id
                                          )
                                        }
                                        else {
                                          appError("There should be Minimum 1 Platform");
                                          return;
                                        }
                                      }
                                      }
                                    >
                                      Remove
                                    </div>
                                  </div>
                                </div>
                                {/* subdiv1 : For Select Perform Access */}
                                <div className="flex flex-col self-start gap-6 self-stretch">
                                  <div className="flex flex-col items-start gap-[8px] self-stretch">
                                    <div className="flex flex-col items-start self-stretch">
                                      <div
                                        className=""
                                        style={{
                                          fontSize: 16,
                                          fontWeight: 700,
                                          fontStyle: "normal",
                                          color: "#1C1C1C",
                                        }}
                                      >
                                        Select Platform Access
                                      </div>
                                      <div
                                        className=""
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 400,
                                          fontStyle: "normal",
                                          color: `${BLACK.B_40}`,
                                          lineHeight: "18px",
                                        }}
                                      >
                                        Choose access permissions based on your
                                        platform
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-4 gap-x-10 gap-y-4 ">
                                      {sub_modulesForPlatform?.map((sub_module: Sub_ModuleForPlatform) => (
                                        <div className="" style={{
                                          display: sub_module?.sub_module_for_platform === "Market Ads" || sub_module?.sub_module_for_platform === "UnMapped Data" ? platform?.platform_id === '2b2c5687-6887-4f4c-9509-4abd7341b81e' ? 'block' : "none" : sub_module?.sub_module_for_platform === "Masters" ? !mastersAccessForPlatforms?.includes(platform?.platform_id) ? "block" : "none" : "block"
                                        }}>
                                          <label className="flex items-center gap-2 pl-2" key={sub_module?.sub_module_for_platform_id}
                                          >
                                            <input
                                              type="checkbox"
                                              name="options"
                                              value="keywordmapping"
                                              checked={platform?.platform_sub_module_array?.some((subModule) => subModule?.sub_module_for_platform_id === sub_module?.sub_module_for_platform_id)}
                                              style={{
                                                width: 16,
                                                height: 16,
                                                accentColor: "black",
                                                cursor: "pointer",
                                              }}
                                              onChange={() =>
                                                submoduleChangeHandlerForPlatform(
                                                  brand?.brand_id,
                                                  platform?.platform_id,
                                                  sub_module?.sub_module_for_platform_id
                                                )
                                              }
                                            />
                                            {sub_module?.sub_module_for_platform}
                                          </label>
                                        </div>
                                      ))}
                                      {/* <label
                                        className="flex items-center gap-2"
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 400,
                                          fontStyle: "normal",
                                          color: "#1C1C1C",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          name="options"
                                          value="keywordmapping"
                                          checked
                                          style={{
                                            width: 16,
                                            height: 16,
                                            accentColor: "black",
                                          }}
                                        />
                                        Favourite Groups
                                      </label> */}
                                    </div>
                                  </div>
                                </div>

                                {/* div for border */}
                                {platform?.dashboard_type?.length !== 0 && (
                                  <div
                                    className=""
                                    style={{
                                      border: `1px solid ${BLACK.B_10}`,
                                      width: "100%",
                                    }}
                                  ></div>
                                )}

                                {/* subdiv2 : Parent Div of All Dashboards */}
                                <div className="gap-10 flex flex-col items-start self-stretch">
                                  {/* Ads_Dashboard Subsection */}
                                  {platform?.dashboard_type?.length > 0 ? (
                                    platform?.dashboard_type?.map(
                                      (dashboard, i) => {
                                        return (
                                          // Single Dashboard Div
                                          <div className="flex flex-col items-start gap-4 self-stretch">
                                            <div
                                              className="flex justify-center items-center gap-3 py-1 pl-3"
                                              style={{
                                                borderLeft: `4px solid ${dashboard.color}`,
                                              }}
                                            >
                                              <div
                                                className=""
                                                style={{
                                                  fontSize: 16,
                                                  fontWeight: 700,
                                                  fontStyle: "normal",
                                                  color: "#1C1C1C",
                                                  lineHeight: "24px",
                                                }}
                                              >
                                                {`${dashboard.dashboard_type_name}`}
                                              </div>
                                              <div className="flex justify-center items-center gap-[8px] rounded-[8px] cursor-pointer">
                                                <div
                                                  className="underline"
                                                  style={{
                                                    fontSize: 14,
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "20px",
                                                    color: STATUS.S_Red,
                                                  }}
                                                  onClick={() =>
                                                    RemoveDashboardHandler(
                                                      brand.brand_id,
                                                      dashboard.dashboard_type_name,
                                                      platform.platform_id,
                                                      dashboard.dashboard_type_id
                                                    )
                                                  }
                                                >
                                                  Remove
                                                </div>
                                              </div>
                                            </div>

                                            {dashboard.dashboard_type_id && (
                                              <SubDashboardsForEdit
                                                dashboard_id={
                                                  dashboard.dashboard_type_id
                                                }
                                                userDashboards={
                                                  dashboard.dashboard_type_sub_module
                                                }
                                                user={user}
                                                brand_id={brand.brand_id}
                                                platform_id={
                                                  platform.platform_id
                                                }
                                                setUser={setUser}
                                                setDataChanged={setDataChanged}
                                              />
                                            )}

                                            {i ==
                                              platform?.dashboard_type?.length -
                                              1 && (
                                                <div
                                                  className="flex h-[40px] justify-center items-center gap-2 rounded-[8px] cursor-pointer"
                                                  onClick={() =>
                                                    addDashboardtoggle(
                                                      brand.brand_id,
                                                      platform.platform_id
                                                    )
                                                  }
                                                >
                                                  <div className="w-[16px] h-[16px]">
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="16"
                                                      height="16"
                                                      viewBox="0 0 16 16"
                                                      fill="none"
                                                    >
                                                      <path
                                                        d="M7.33325 8.66671H3.99992C3.81103 8.66671 3.6527 8.60282 3.52492 8.47504C3.39714 8.34726 3.33325 8.18893 3.33325 8.00004C3.33325 7.81115 3.39714 7.65282 3.52492 7.52504C3.6527 7.39726 3.81103 7.33337 3.99992 7.33337H7.33325V4.00004C7.33325 3.81115 7.39714 3.65282 7.52492 3.52504C7.6527 3.39726 7.81103 3.33337 7.99992 3.33337C8.18881 3.33337 8.34714 3.39726 8.47492 3.52504C8.6027 3.65282 8.66659 3.81115 8.66659 4.00004V7.33337H11.9999C12.1888 7.33337 12.3471 7.39726 12.4749 7.52504C12.6027 7.65282 12.6666 7.81115 12.6666 8.00004C12.6666 8.18893 12.6027 8.34726 12.4749 8.47504C12.3471 8.60282 12.1888 8.66671 11.9999 8.66671H8.66659V12C8.66659 12.1889 8.6027 12.3473 8.47492 12.475C8.34714 12.6028 8.18881 12.6667 7.99992 12.6667C7.81103 12.6667 7.6527 12.6028 7.52492 12.475C7.39714 12.3473 7.33325 12.1889 7.33325 12V8.66671Z"
                                                        fill="#1886FE"
                                                      />
                                                    </svg>
                                                  </div>
                                                  <div
                                                    className="underline"
                                                    style={{
                                                      fontSize: "14px",
                                                      fontStyle: "normal",
                                                      fontWeight: 500,
                                                      lineHeight: "20px",
                                                      color: PRIMARY.P_BLue,
                                                    }}
                                                  >
                                                    Add Dashboard
                                                  </div>
                                                </div>
                                              )}
                                          </div>
                                        );
                                      }
                                    )
                                  ) : (
                                    <div
                                      className="flex h-[40px] justify-center items-center gap-2 rounded-[8px] cursor-pointer"
                                      onClick={() =>
                                        addDashboardtoggle(
                                          brand.brand_id,
                                          platform.platform_id
                                        )
                                      }
                                    >
                                      <div className="w-[16px] h-[16px]">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                        >
                                          <path
                                            d="M7.33325 8.66671H3.99992C3.81103 8.66671 3.6527 8.60282 3.52492 8.47504C3.39714 8.34726 3.33325 8.18893 3.33325 8.00004C3.33325 7.81115 3.39714 7.65282 3.52492 7.52504C3.6527 7.39726 3.81103 7.33337 3.99992 7.33337H7.33325V4.00004C7.33325 3.81115 7.39714 3.65282 7.52492 3.52504C7.6527 3.39726 7.81103 3.33337 7.99992 3.33337C8.18881 3.33337 8.34714 3.39726 8.47492 3.52504C8.6027 3.65282 8.66659 3.81115 8.66659 4.00004V7.33337H11.9999C12.1888 7.33337 12.3471 7.39726 12.4749 7.52504C12.6027 7.65282 12.6666 7.81115 12.6666 8.00004C12.6666 8.18893 12.6027 8.34726 12.4749 8.47504C12.3471 8.60282 12.1888 8.66671 11.9999 8.66671H8.66659V12C8.66659 12.1889 8.6027 12.3473 8.47492 12.475C8.34714 12.6028 8.18881 12.6667 7.99992 12.6667C7.81103 12.6667 7.6527 12.6028 7.52492 12.475C7.39714 12.3473 7.33325 12.1889 7.33325 12V8.66671Z"
                                            fill="#1886FE"
                                          />
                                        </svg>
                                      </div>
                                      <div
                                        className="underline"
                                        style={{
                                          fontSize: "14px",
                                          fontStyle: "normal",
                                          fontWeight: 500,
                                          lineHeight: "20px",
                                          color: PRIMARY.P_BLue,
                                        }}
                                      >
                                        Add Dashboard
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {dataFetchingState === "LOADING" && (
        <div
          className="flex flex-col items-start gap-6 pt-4 mb-10"
          style={{
            height: "calc(100vh - 72px)",
            overflow: "scroll",
            scrollbarWidth: "none",
          }}
        >
          <div className="flex w-fit justify-between items-center mx-10 h-10">
            <div className="flex flex-row items-center">
              <span
                // onClick={() => navigate("/userManagement/userList")}
                onClick={() => cancelToggleHandler("userList")}
                className="cursor-pointer"
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: BLACK.B_40,
                }}
              >
                User List
              </span>
              <span className="mx-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8.40003 7.99999L5.80003 5.39999C5.67781 5.27777 5.6167 5.12221 5.6167 4.93333C5.6167 4.74444 5.67781 4.58888 5.80003 4.46666C5.92225 4.34444 6.07781 4.28333 6.2667 4.28333C6.45559 4.28333 6.61114 4.34444 6.73337 4.46666L9.80003 7.53333C9.8667 7.59999 9.91392 7.67221 9.9417 7.74999C9.96948 7.82777 9.98337 7.9111 9.98337 7.99999C9.98337 8.08888 9.96948 8.17221 9.9417 8.24999C9.91392 8.32777 9.8667 8.39999 9.80003 8.46666L6.73337 11.5333C6.61114 11.6555 6.45559 11.7167 6.2667 11.7167C6.07781 11.7167 5.92225 11.6555 5.80003 11.5333C5.67781 11.4111 5.6167 11.2555 5.6167 11.0667C5.6167 10.8778 5.67781 10.7222 5.80003 10.6L8.40003 7.99999Z"
                    fill="#1C1C1C"
                    fill-opacity="0.4"
                  />
                </svg>
              </span>
              <span
                // onClick={() => navigate(`/userManagement/userDetailView/${user_id}`)}
                onClick={() => cancelToggleHandler("userDetailsView")}
                className="cursor-pointer"
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: "18px",
                  color: BLACK.B_40,
                }}
              >
                User Details
              </span>
              <span className="mx-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8.40003 7.99999L5.80003 5.39999C5.67781 5.27777 5.6167 5.12221 5.6167 4.93333C5.6167 4.74444 5.67781 4.58888 5.80003 4.46666C5.92225 4.34444 6.07781 4.28333 6.2667 4.28333C6.45559 4.28333 6.61114 4.34444 6.73337 4.46666L9.80003 7.53333C9.8667 7.59999 9.91392 7.67221 9.9417 7.74999C9.96948 7.82777 9.98337 7.9111 9.98337 7.99999C9.98337 8.08888 9.96948 8.17221 9.9417 8.24999C9.91392 8.32777 9.8667 8.39999 9.80003 8.46666L6.73337 11.5333C6.61114 11.6555 6.45559 11.7167 6.2667 11.7167C6.07781 11.7167 5.92225 11.6555 5.80003 11.5333C5.67781 11.4111 5.6167 11.2555 5.6167 11.0667C5.6167 10.8778 5.67781 10.7222 5.80003 10.6L8.40003 7.99999Z"
                    fill="#1C1C1C"
                    fill-opacity="0.4"
                  />
                </svg>
              </span>
              <span
                style={{ fontSize: 12, fontWeight: 500, lineHeight: "18px" }}
              >
                Edit Details
              </span>
            </div>
          </div>
          <div className="w-full mx-10" style={{ height: 412 }}>
            <Skeleton
              sx={{
                height: 412,
                borderRadius: 16,
                width: "calc(100% - 80px)",
                opacity: 0.5,
              }}
            />
          </div>
          {/* <div className="w-full mx-10" style={{ height: 635 }}>
            <Skeleton
              sx={{
                height: 635,
                borderRadius: 16,
                width: "calc(100% - 80px)",
                opacity: 0.5,
              }}
            />
          </div>  */}
        </div>
      )}

      {dataFetchingState === "SUCCESS" && (
        <div
          className=" w-full flex px-10 py-4 justify-end items-center gap-4"
          style={{
            boxShadow: "0px -4px 40px 4px rgba(0,0,0,0.05)",
            backgroundColor: `${WHITE.W_100}`,
            position: "sticky",
            bottom: "0px",
          }}
        >
          <Button
            color="neutral"
            sx={{
              height: "40px",
              width: "16.17%",
              backgroundColor: BLACK.B_5,
              color: BLACK.B_100,
              ":hover": {
                backgroundColor: BLACK.B_20,
              },
              padding: "8px 16px",
            }}
            onClick={() => cancelToggleHandler("")}
          >
            Cancel
          </Button>
          <Button
            onClick={() => saveuser()}
            disabled={!DataChanged && true}
            loading={loading}
            sx={{
              height: "40px",
              width: "16.17%",
              backgroundColor: PRIMARY.P_BLue,
              padding: "8px 16px",
            }}
          >
            Save & Publish
          </Button>
        </div>
      )}

      {removedata?.remove_toggle && (
        <RemoveBrandModal
          removedata={removedata}
          setRemoveData={setRemoveData}
          remove={remove}
        />
      )}
      {cancelToggle && (
        <SaveChangesModal
          cancelSaveChangesToggle={cancelSaveChanges}
          savechanges={savechanges}
          loading={loading}
        />
      )}
      {toggleModal && user_id && (
        <UserPlatformEditModal
          brands={brands}
          setBrands={setBrands}
          setToggleModal={setToggleModal}
          setRefresh={setRefresh}
          view={view}
          setView={setView}
          user_id={user_id}
          user={structuredClone(user)}
          setUser={setUser}
          selectedBrandId={selectedBrandId}
          setSelectedBrandId={setSelectedBrandId}
          selPlatforms={selPlatforms}
          setSelPlatforms={setSelPlatforms}
          setDataChanged={setDataChanged}
          sub_modules={sub_modules}
        />
      )}
    </>
  );
};

// Default export
export default React.memo(UserDetailEditNew);
